import React from "react";
import { Star, StarOff, Copy, X } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { TooltipWrapper } from "../../../components/ui/tooltip-wrapper";

interface ConfigActionsProps {
	configId: string;
	isDefault: boolean;
	variant: "compact" | "full";
	onToggleDefault: () => void;
	onDuplicate: () => void;
	onDelete: () => void;
}

export const ConfigActions: React.FC<ConfigActionsProps> = ({
	configId,
	isDefault,
	variant,
	onToggleDefault,
	onDuplicate,
	onDelete,
}) => {
	const defaultLabel = isDefault
		? "Standard-Ansicht entfernen"
		: "Als Standard festlegen";

	const handleClick =
		(handler: () => void) => (e: React.MouseEvent<HTMLButtonElement>) => {
			e.stopPropagation();
			handler();
		};

	if (variant === "full") {
		return (
			<div className="flex items-center gap-1">
				<Button
					size="sm"
					variant="ghost"
					className="h-8 gap-1.5"
					onClick={handleClick(onToggleDefault)}
					aria-label={defaultLabel}
					data-testid={`config-action-default-${configId}`}
				>
					{isDefault ? (
						<StarOff className="h-4 w-4 text-yellow-500" />
					) : (
						<Star className="h-4 w-4" />
					)}
					<span className="text-xs">{defaultLabel}</span>
				</Button>
				<Button
					size="sm"
					variant="ghost"
					className="h-8 gap-1.5"
					onClick={handleClick(onDuplicate)}
					aria-label="Ansicht duplizieren"
					data-testid={`config-action-duplicate-${configId}`}
				>
					<Copy className="h-4 w-4" />
					<span className="text-xs">Ansicht duplizieren</span>
				</Button>
				<Button
					size="sm"
					variant="ghost"
					className="h-8 gap-1.5 text-red-600 hover:text-red-700 hover:bg-red-50"
					onClick={handleClick(onDelete)}
					aria-label="Ansicht löschen"
					data-testid={`config-action-delete-${configId}`}
				>
					<X className="h-4 w-4" />
					<span className="text-xs">Ansicht löschen</span>
				</Button>
			</div>
		);
	}

	return (
		<div className="flex items-center gap-1 min-w-[84px] justify-end">
			<TooltipWrapper title={defaultLabel}>
				<Button
					size="sm"
					variant="ghost"
					className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity"
					onClick={handleClick(onToggleDefault)}
					aria-label={defaultLabel}
					data-testid={`config-action-default-${configId}`}
				>
					{isDefault ? (
						<StarOff className="h-3 w-3 text-yellow-500" />
					) : (
						<Star className="h-3 w-3" />
					)}
				</Button>
			</TooltipWrapper>
			<TooltipWrapper title="Ansicht duplizieren">
				<Button
					size="sm"
					variant="ghost"
					className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity"
					onClick={handleClick(onDuplicate)}
					aria-label="Ansicht duplizieren"
					data-testid={`config-action-duplicate-${configId}`}
				>
					<Copy className="h-3 w-3" />
				</Button>
			</TooltipWrapper>
			<TooltipWrapper title="Ansicht löschen">
				<Button
					size="sm"
					variant="ghost"
					className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity"
					onClick={handleClick(onDelete)}
					aria-label="Ansicht löschen"
					data-testid={`config-action-delete-${configId}`}
				>
					<X className="h-3 w-3" />
				</Button>
			</TooltipWrapper>
		</div>
	);
};
